// All artwork UI in one place: the shared <artwork-card> tile plus the two
// surfaces that render it — <artworks-modal> (a picker dialog) and
// <artworks-gallery> (inline list on the profile page). Data access lives
// separately in @theme/artworks-api.
//
//   <artworks-modal customer-id="123">
//     <dialog ref="dialog" class="artworks-modal__dialog">
//       <header class="artworks-modal__header">
//         <h2>Your artworks</h2>
//         <button type="button" on:click="artworks-modal/closeDialog" aria-label="Close">&times;</button>
//       </header>
//       <p ref="status" class="artworks-modal__status"></p>
//       <div ref="grid" class="artworks-modal__grid"></div>
//     </dialog>
//   </artworks-modal>
//
//   <artworks-gallery customer-id="123" empty-text="No artworks yet.">
//     <p ref="status" class="sb-profile__artworks-status"></p>
//     <div ref="grid" class="sb-profile__artworks-grid"></div>
//   </artworks-gallery>

import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { debounce, fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';
import { ArtworksApi } from '@theme/artworks-api';

/** @typedef {import('@theme/artworks-api').Artwork} Artwork */
/** @typedef {import('@theme/artworks-api').PageInfo} PageInfo */

/**
 * @typedef {object} ArtworkCardClasses
 * @property {string} [figure]
 * @property {string} [image]
 * @property {string} [caption]
 * @property {string} [media] - Positioned wrapper the badges overlay.
 * @property {string} [badge] - Base class; `--start` / `--end` modifiers are appended.
 */

export class ArtworkSelectedEvent extends CustomEvent {
  static eventName = 'artwork:selected';

  /**
   * `width`/`height` are the artwork's pixel dimensions where known — they let the
   * receiving <artwork-actions> re-check print resolution when the size changes
   * later. Absent for artwork we've never measured. `mimeType` rides along so that
   * re-check knows what it's looking at (animated images can't be upscaled).
   *
   * @param {{ id?: string, fileUrl: string, previewUrl: string, width?: number, height?: number, mimeType?: string, extension?: string }} detail
   */
  constructor(detail) {
    super(ArtworkSelectedEvent.eventName, { bubbles: true, composed: true, detail });
  }
}

/**
 * Builds the inner figure markup for a single artwork preview. Kept separate
 * from the card element so each surface can pass its own BEM class names.
 *
 * @param {{ id: string, previewUrl: string, name?: string, width?: number | null, height?: number | null, extension?: string | null }} artwork
 * @param {ArtworkCardClasses} [classes]
 * @returns {HTMLElement}
 */
export function createArtworkFigure(artwork, classes = {}) {
  const {
    figure: figureClass = 'artworks-modal__card',
    image: imageClass = 'artworks-modal__preview',
    caption: captionClass = 'artworks-modal__id',
    media: mediaClass = 'artworks-modal__media',
    badge: badgeClass = 'artworks-modal__badge',
  } = classes;

  const label = artwork.name || `#${artwork.id}`;

  const figure = document.createElement('figure');
  figure.className = figureClass;

  const img = document.createElement('img');
  img.className = imageClass;
  img.src = artwork.previewUrl;
  img.alt = label;
  img.loading = 'lazy';

  // The badges overlay the image specifically, so they need their own
  // positioned box — the figure also holds the caption, which sits below it.
  const media = document.createElement('div');
  media.className = mediaClass;
  media.append(img);

  if (artwork.width && artwork.height) {
    media.append(createBadge(`${artwork.width} × ${artwork.height}`, `${badgeClass} ${badgeClass}--start`));
  }
  if (artwork.extension) {
    media.append(createBadge(artwork.extension, `${badgeClass} ${badgeClass}--end`));
  }

  const caption = document.createElement('figcaption');
  caption.className = captionClass;
  caption.textContent = label;

  figure.append(media, caption);
  return figure;
}

/**
 * A small overlay label on an artwork preview (pixel size, file type).
 *
 * @param {string} text
 * @param {string} className
 * @returns {HTMLElement}
 */
function createBadge(text, className) {
  const badge = document.createElement('span');
  badge.className = className;
  badge.textContent = text;
  return badge;
}

/**
 * Builds an <artwork-card> element for the given artwork. Pass
 * `selectable: true` to make it a clickable, keyboard-operable tile that
 * dispatches `artwork:selected`.
 *
 * @param {Artwork} artwork
 * @param {object} [options]
 * @param {boolean} [options.selectable]
 * @param {boolean} [options.editable] - Adds an inline rename affordance.
 * @param {string | null} [options.customerId] - Needed by editable cards to persist renames.
 * @param {ArtworkCardClasses} [options.classes]
 * @returns {ArtworkCard}
 */
export function createArtworkCard(artwork, { selectable = false, editable = false, customerId, classes } = {}) {
  const card = /** @type {ArtworkCard} */ (document.createElement('artwork-card'));
  card.setAttribute('artwork-id', artwork.id);
  card.setAttribute('preview-url', artwork.previewUrl);
  card.setAttribute('file-url', artwork.fileUrl);
  if (artwork.name) card.setAttribute('name', artwork.name);
  // Only present on artwork uploaded since dimensions were tracked; without them
  // a gallery pick simply can't be resolution-checked.
  if (artwork.width) card.setAttribute('width', String(artwork.width));
  if (artwork.height) card.setAttribute('height', String(artwork.height));
  if (artwork.mimeType) card.setAttribute('mime-type', artwork.mimeType);
  if (artwork.extension) card.setAttribute('extension', artwork.extension);
  if (selectable) card.setAttribute('selectable', '');
  if (editable) {
    card.setAttribute('editable', '');
    if (customerId) card.setAttribute('customer-id', customerId);
  }

  card.append(createArtworkFigure(artwork, classes));
  return card;
}

/**
 * A single artwork tile. Display-only by default; add the `selectable`
 * attribute to make it a clickable, keyboard-operable control that dispatches
 * an `artwork:selected` event (bubbling) when chosen.
 */
export class ArtworkCard extends Component {
  #editing = false;
  /** @type {HTMLElement | null} */
  #caption = null;
  /** @type {HTMLElement | null} */
  #editForm = null;
  /** @type {HTMLInputElement | null} */
  #editInput = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.selectable) this.#enableSelection();
    if (this.editable) this.#enableEditing();
  }

  get selectable() {
    return this.hasAttribute('selectable');
  }

  get editable() {
    return this.hasAttribute('editable');
  }

  get artworkId() {
    return this.getAttribute('artwork-id');
  }

  get artworkName() {
    return this.getAttribute('name') ?? '';
  }

  get customerId() {
    return this.getAttribute('customer-id');
  }

  get fileUrl() {
    return this.getAttribute('file-url');
  }

  get previewUrl() {
    return this.getAttribute('preview-url');
  }

  /** @returns {number | undefined} Pixel width, when it was recorded at upload. */
  get pixelWidth() {
    return Number(this.getAttribute('width')) || undefined;
  }

  /** @returns {number | undefined} */
  get pixelHeight() {
    return Number(this.getAttribute('height')) || undefined;
  }

  /** @returns {string | undefined} e.g. "image/png" */
  get mimeType() {
    return this.getAttribute('mime-type') || undefined;
  }

  /** @returns {string | undefined} e.g. "png" */
  get extension() {
    return this.getAttribute('extension') || undefined;
  }

  /**
   * Turns the card into an accessible button and wires up the framework's
   * delegated click/keydown handling.
   */
  #enableSelection() {
    this.setAttribute('role', 'button');
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
    if (!this.hasAttribute('aria-label')) {
      this.setAttribute('aria-label', `Select artwork ${this.artworkId ?? ''}`.trim());
    }
    this.setAttribute('on:click', 'artwork-card/select');
    this.setAttribute('on:keydown', 'artwork-card/handleKeydown');
  }

  select() {
    if (!this.selectable) return;

    this.dispatchEvent(
      new ArtworkSelectedEvent({
        id: this.artworkId ?? '',
        fileUrl: this.fileUrl ?? '',
        previewUrl: this.previewUrl ?? '',
        width: this.pixelWidth,
        height: this.pixelHeight,
        mimeType: this.mimeType,
        extension: this.extension,
      })
    );
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    this.select();
  }

  /** Adds an always-visible pencil button that opens the rename input. */
  #enableEditing() {
    const pencil = document.createElement('button');
    pencil.type = 'button';
    pencil.className = 'artwork-card__edit';
    pencil.setAttribute('aria-label', 'Rename artwork');
    pencil.setAttribute('on:click', 'artwork-card/startEdit');
    pencil.innerHTML =
      '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M13.5 3.5a1.65 1.65 0 0 1 2.33 2.33l-8.2 8.2-3.13.8.8-3.13 8.2-8.2Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>';
    this.prepend(pencil);
  }

  /** @param {Event} [event] */
  startEdit(event) {
    event?.stopPropagation();
    if (this.#editing) return;

    const caption = this.querySelector('figcaption');
    if (!(caption instanceof HTMLElement)) return;

    this.#editing = true;
    this.#caption = caption;
    caption.hidden = true;

    const form = document.createElement('div');
    form.className = 'artwork-card__edit-form';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'artwork-card__edit-input';
    input.value = this.artworkName;
    input.placeholder = 'Artwork name';
    input.setAttribute('aria-label', 'Artwork name');
    input.setAttribute('on:keydown', 'artwork-card/handleEditKeydown');

    const save = document.createElement('button');
    save.type = 'button';
    save.className = 'artwork-card__save';
    save.textContent = 'Save';
    save.setAttribute('on:click', 'artwork-card/saveEdit');

    form.append(input, save);
    caption.after(form);

    this.#editForm = form;
    this.#editInput = input;
    input.focus();
    input.select();
  }

  /** @param {KeyboardEvent} event */
  handleEditKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveEdit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit();
    }
  }

  /** @param {Event} [event] */
  async saveEdit(event) {
    event?.stopPropagation();

    const input = this.#editInput;
    const save = /** @type {HTMLButtonElement | null} */ (this.#editForm?.querySelector('.artwork-card__save') ?? null);
    if (!input) return;

    const name = input.value.trim();
    if (!name) {
      // Names are required — keep editing.
      input.setAttribute('aria-invalid', 'true');
      input.focus();
      return;
    }

    input.removeAttribute('aria-invalid');
    input.disabled = true;
    if (save) {
      save.disabled = true;
      save.textContent = 'Saving…';
    }

    try {
      await new ArtworksApi({ customerId: this.customerId }).rename({ id: this.artworkId ?? '', name });
      this.setAttribute('name', name);
      this.#applyName(name);
      this.#finishEdit();
    } catch (error) {
      console.error(error);
      input.disabled = false;
      if (save) {
        save.disabled = false;
        save.textContent = 'Save';
      }
      input.setAttribute('aria-invalid', 'true');
      input.focus();
    }
  }

  /** @param {Event} [event] */
  cancelEdit(event) {
    event?.stopPropagation();
    this.#finishEdit();
  }

  /** @param {string} name */
  #applyName(name) {
    const label = name || `#${this.artworkId ?? ''}`;
    if (this.#caption) this.#caption.textContent = label;
    const img = this.querySelector('img');
    if (img instanceof HTMLImageElement) img.alt = label;
  }

  /** Tears down the edit form, restores the caption, and refocuses the pencil. */
  #finishEdit() {
    this.#editing = false;
    this.#editForm?.remove();
    this.#editForm = null;
    this.#editInput = null;
    if (this.#caption) this.#caption.hidden = false;
    /** @type {HTMLElement | null} */ (this.querySelector('.artwork-card__edit'))?.focus();
  }
}

if (!customElements.get('artwork-card')) customElements.define('artwork-card', ArtworkCard);

/** Raster types the browser can decode well enough to measure. */
const CHECKABLE_RASTER_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
  'image/bmp',
  'image/gif',
]);

/** DPI a raster upload must reach unless `required-dpi` says otherwise. */
const DEFAULT_REQUIRED_DPI = 300;

/** Cloudinary's e_upscale refuses inputs at or above 4.2 megapixels. */
const UPSCALE_MAX_INPUT_PIXELS = 4.2e6;

/** e_upscale enlarges each dimension by a fixed 4x, so it can't go further. */
const UPSCALE_MAX_FACTOR = 4;

/**
 * Decodes a raster just far enough to read its pixel dimensions. Returns null
 * when the browser can't decode it — the DPI check then fails open rather than
 * blocking an upload we were never able to measure.
 *
 * @param {File} file
 * @returns {Promise<{ width: number, height: number } | null>}
 */
async function readImageSize(file) {
  if (typeof createImageBitmap === 'function') {
    /** @type {ImageBitmap | null} */
    let bitmap = null;
    try {
      bitmap = await createImageBitmap(file);
      return { width: bitmap.width, height: bitmap.height };
    } catch {
      // Unsupported by createImageBitmap — try the <img> path below.
    } finally {
      bitmap?.close();
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
      image.src = url;
    });
    return { width: image.naturalWidth, height: image.naturalHeight };
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * @typedef {object} DpiResult
 * @property {number} dpi - Effective print resolution at the chosen size.
 * @property {boolean} ok
 * @property {{ width: number, height: number }} recommendedPx
 * @property {{ possible: boolean, reason: string, targetWidth: number, targetHeight: number }} upscale
 */

/**
 * Works out the resolution a raster will actually print at, and whether
 * Cloudinary could upscale it to the target.
 *
 * Artwork is placed "contain" — scaled uniformly to fit the W×H bounding box
 * with nothing cropped — so one scale factor applies to the whole image and the
 * effective resolution is `max(pw / W, ph / H)`. Any DPI recorded in the file's
 * own metadata (JFIF density, PNG pHYs, EXIF) is meaningless here: we rescale to
 * a physical size the customer picked, so only the pixel count matters.
 *
 * @param {object} params
 * @param {{ width: number, height: number }} params.pixels
 * @param {{ widthIn: number, heightIn: number }} params.dimensions
 * @param {number} params.requiredDpi
 * @param {string} [params.type] - The file's MIME type.
 * @returns {DpiResult}
 */
function evaluateDpi({ pixels, dimensions, requiredDpi, type = '' }) {
  const { width: pw, height: ph } = pixels;
  const { widthIn, heightIn } = dimensions;

  const dpi = Math.max(pw / widthIn, ph / heightIn);
  const ok = dpi >= requiredDpi;
  const factor = requiredDpi / dpi;

  let reason = '';
  if (type === 'image/gif') reason = 'animated';
  else if (pw * ph >= UPSCALE_MAX_INPUT_PIXELS) reason = 'too-large';
  else if (factor > UPSCALE_MAX_FACTOR) reason = 'too-low';

  return {
    dpi,
    ok,
    recommendedPx: {
      width: Math.ceil(requiredDpi * widthIn),
      height: Math.ceil(requiredDpi * heightIn),
    },
    upscale: {
      possible: !ok && !reason,
      reason,
      targetWidth: Math.ceil(pw * factor),
      targetHeight: Math.ceil(ph * factor),
    },
  };
}

/**
 * Customer-facing copy for a failed DPI check.
 *
 * @param {DpiResult} result
 * @param {{ widthIn: number, heightIn: number }} dimensions
 * @param {number} requiredDpi
 * @param {object} [options]
 * @param {boolean} [options.inline] - True for the PDP notice, which has no upscale button of its own.
 * @returns {string}
 */
function dpiWarningText(result, dimensions, requiredDpi, { inline = false } = {}) {
  const inches = (value) => String(Number(value.toFixed(2)));
  const size = `${inches(dimensions.widthIn)}″ × ${inches(dimensions.heightIn)}″`;
  const lead = `This file works out to about ${Math.round(result.dpi)} DPI at ${size}. We recommend ${requiredDpi} DPI for a sharp print.`;
  const minimum = `For that size, please upload at least ${result.recommendedPx.width} × ${result.recommendedPx.height} px.`;

  if (result.upscale.possible) {
    return inline
      ? `${lead} Upload it again to upscale it, or choose a higher-resolution file.`
      : `${lead} We can upscale it for you, or you can choose a higher-resolution file.`;
  }

  switch (result.upscale.reason) {
    case 'animated':
      return `${lead} Animated images can't be upscaled. ${minimum}`;
    case 'too-large':
      return `${lead} It's too large for our upscaler to process. ${minimum}`;
    default:
      return `${lead} It's too far below the target for our upscaler to fix. ${minimum}`;
  }
}

/**
 * Renders a "Retry" affordance into the status element after a failed load.
 *
 * @param {HTMLElement} status
 * @param {string} onClick - The `on:click` binding to attach to the button.
 */
function renderLoadError(status, onClick) {
  status.replaceChildren();

  const message = document.createElement('span');
  message.textContent = 'Failed to load artworks. ';

  const retry = document.createElement('button');
  retry.type = 'button';
  retry.textContent = 'Retry';
  retry.setAttribute('on:click', onClick);

  status.append(message, retry);
}

/**
 * @typedef {object} ArtworksModalRefs
 * @property {HTMLDialogElement} dialog
 * @property {ArtworksGallery} gallery
 * @property {HTMLButtonElement} galleryTab
 * @property {HTMLButtonElement} uploadTab
 * @property {HTMLElement} galleryPanel
 * @property {HTMLElement} uploadPanel
 * @property {HTMLInputElement} fileInput
 * @property {HTMLButtonElement} uploadButton
 * @property {HTMLElement} uploadStatus
 * @property {HTMLElement} [dpiWarning]
 * @property {HTMLElement} [dpiMessage]
 * @property {HTMLButtonElement} [dpiUpscale]
 */

/**
 * A dialog with two tabs — "Gallery" (an embedded selectable <artworks-gallery>)
 * and "Upload" (manual file upload). Reports a chosen gallery artwork via an
 * `artwork:selected` event.
 *
 * @extends DialogComponent<ArtworksModalRefs>
 */
export class ArtworksModal extends DialogComponent {
  requiredRefs = [
    'dialog',
    'gallery',
    'galleryTab',
    'uploadTab',
    'galleryPanel',
    'uploadPanel',
    'fileInput',
    'uploadButton',
    'uploadStatus',
  ];

  /** The element that opened the modal and should be told about a selection. */
  /** @type {HTMLElement | null} */
  #requester = null;

  /** A file held back by the DPI check, awaiting an upscale or a replacement. */
  /** @type {{ file: File, pixels: { width: number, height: number } | null, upscale: DpiResult['upscale'] } | null} */
  #pending = null;

  /** True while an upscale/upload is in flight. */
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    // Route a selection back to whoever opened the modal, then close.
    this.addEventListener(ArtworkSelectedEvent.eventName, this.#handleSelected);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ArtworkSelectedEvent.eventName, this.#handleSelected);
  }

  /**
   * Forwards the chosen artwork to the opener (so it can attach it to its own
   * form), then closes. Fires for both gallery picks and fresh uploads.
   *
   * @param {ArtworkSelectedEvent} event
   */
  #handleSelected = (event) => {
    const requester = this.#requester;
    this.#requester = null;
    this.closeDialog();

    if (requester && requester !== event.target) {
      requester.dispatchEvent(new ArtworkSelectedEvent({ ...event.detail }));
    }
  };

  get customerId() {
    return this.getAttribute('customer-id');
  }

  get #api() {
    return new ArtworksApi({ customerId: this.customerId });
  }

  /** @returns {HTMLButtonElement[]} */
  get #tabs() {
    return [this.refs.galleryTab, this.refs.uploadTab];
  }

  /**
   * Opens the modal on the given tab with a clean upload state.
   *
   * @param {'gallery' | 'upload'} [defaultTab] - Defaults to the Gallery tab.
   * @param {HTMLElement | null} [requester] - Notified via `artwork:selected` when a choice is made.
   */
  open(defaultTab = 'gallery', requester = null) {
    this.#requester = requester;
    const tab = defaultTab === 'upload' ? this.refs.uploadTab : this.refs.galleryTab;
    this.#activateTab(tab, false);
    this.#resetUpload();
    this.showDialog();
    this.refs.gallery.refresh();
  }

  /** @param {Event} event */
  selectTab(event) {
    const tab = /** @type {HTMLElement} */ (event.target).closest('[role="tab"]');
    if (tab instanceof HTMLButtonElement) this.#activateTab(tab);
  }

  /** @param {KeyboardEvent} event */
  handleTabKeydown(event) {
    const tabs = this.#tabs;
    const current = tabs.indexOf(
      /** @type {HTMLButtonElement} */ (/** @type {HTMLElement} */ (event.target).closest('[role="tab"]'))
    );
    if (current === -1) return;

    let next = current;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#activateTab(tabs[current]);
        return;
      default:
        return;
    }

    event.preventDefault();
    this.#activateTab(tabs[next]);
  }

  /**
   * Activates a tab: updates ARIA state, roving tabindex, panel visibility,
   * and (optionally) moves focus to the tab.
   *
   * @param {HTMLButtonElement} tab
   * @param {boolean} [focus]
   */
  #activateTab(tab, focus = true) {
    for (const candidate of this.#tabs) {
      const selected = candidate === tab;
      candidate.setAttribute('aria-selected', String(selected));
      candidate.tabIndex = selected ? 0 : -1;

      const panel = document.getElementById(candidate.getAttribute('aria-controls') ?? '');
      if (panel) panel.hidden = !selected;
    }

    if (focus) tab.focus();
  }

  /** Opens the native file picker for the upload tab. */
  openFilePicker() {
    this.refs.fileInput.click();
  }

  /**
   * Gate, then upload. Rasters going onto a product with a known print size
   * have to clear the required DPI first; everything else uploads straight away.
   *
   * @param {Event} event
   */
  async handleFileSelected(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const [file] = input.files ?? [];
    // Clear immediately so re-picking the same file still fires `change` —
    // after a rejection the customer often retries the same file via the
    // panel's "Upload a file" button.
    input.value = '';
    if (!file) return;

    this.#clearWarning();

    // Measured once, here, and then carried all the way through to create() —
    // so a size change later on can be re-checked without re-reading the file.
    const pixels = CHECKABLE_RASTER_TYPES.has(file.type) ? await readImageSize(file) : null;

    const check = this.#checkResolution(file, pixels);
    if (!check) {
      await this.#uploadAndSelect(file, pixels);
      return;
    }

    this.#pending = { file, pixels, upscale: check.result.upscale };
    this.#showWarning(check);
  }

  /**
   * Runs the DPI check for a file. Returns null when the check doesn't apply —
   * the form tracks no print size, the file isn't a measurable raster (vector,
   * PDF and PSD have no meaningful DPI), it wouldn't decode, or it passed.
   * Failing open is deliberate: never block an upload we couldn't measure.
   *
   * @param {File} file
   * @param {{ width: number, height: number } | null} pixels
   * @returns {{ result: DpiResult, dimensions: { widthIn: number, heightIn: number }, requiredDpi: number } | null}
   */
  #checkResolution(file, pixels) {
    const requester = /** @type {ArtworkActions | null} */ (this.#requester);
    const dimensions = requester?.printDimensions;
    if (!dimensions || !pixels) return null;

    const requiredDpi = requester.requiredDpi;
    const result = evaluateDpi({ pixels, dimensions, requiredDpi, type: file.type });

    return result.ok ? null : { result, dimensions, requiredDpi };
  }

  /**
   * Sends the held-back file through Cloudinary's generative upscaler, then
   * uploads the result like any other file — the print file still lands in GCS.
   */
  async upscaleAndUpload() {
    const pending = this.#pending;
    if (!pending?.upscale.possible) return;

    // The warning has done its job the moment the customer commits to the
    // upscale — hand the panel over to the status line so progress reads as one
    // continuous sequence (upscaling → uploading) instead of two competing
    // messages.
    this.#clearWarning();

    const { uploadStatus } = this.refs;
    this.#setUploadBusy(true);
    uploadStatus.textContent = 'Upscaling your artwork…';

    try {
      const upscaled = await this.#api.upscaleFile(pending.file, {
        targetWidth: pending.upscale.targetWidth,
        targetHeight: pending.upscale.targetHeight,
      });
      // Re-measure rather than assume the target: what's stored has to match
      // the file, or later re-checks would be based on a fiction.
      await this.#uploadAndSelect(upscaled, await readImageSize(upscaled));
    } catch (error) {
      console.error(error);
      // Upscaling is best-effort. When it fails the customer still needs a
      // better file, so fall back to that ask.
      uploadStatus.textContent = "We couldn't upscale that file. Please upload a higher-resolution one.";
    } finally {
      this.#setUploadBusy(false);
    }
  }

  /**
   * Accepts the held-back file as-is. The DPI check is advisory — the customer
   * has seen the warning and chosen the trade-off — so this joins the same path
   * a passing file takes.
   */
  async continueWithFile() {
    const pending = this.#pending;
    if (!pending) return;

    // Capture first: #clearWarning() nulls #pending.
    this.#clearWarning();
    await this.#uploadAndSelect(pending.file, pending.pixels);
  }

  /**
   * Stores a file and reports it as the current selection. Shared by the plain
   * upload path and the post-upscale one.
   *
   * @param {File} file
   * @param {{ width: number, height: number } | null} [pixels] - Recorded on the artwork so later size changes can be re-checked.
   */
  async #uploadAndSelect(file, pixels = null) {
    const { uploadStatus } = this.refs;
    this.#setUploadBusy(true);
    uploadStatus.textContent = `Uploading ${file.name}…`;

    try {
      const fields = await this.#api.uploadFile(file);
      const mimeType = file.type || undefined;
      
      const extensionMatch = /\.([a-z0-9]+)$/i.exec((file.name ?? '').trim());
     
      const extension = extensionMatch ? extensionMatch[1].toLowerCase() : '';

      const artwork = await this.#api.create({
        ...fields,
        width: pixels?.width,
        height: pixels?.height,
        mimeType,
        extension,
      });

      // Keep the gallery in sync for the next time the modal opens.
      this.refs.gallery.refresh();

      // A fresh upload counts as a selection. This also closes the modal via
      // the artwork:selected listener wired up in connectedCallback.
      this.dispatchEvent(
        new ArtworkSelectedEvent({
          id: artwork?.id ?? '',
          fileUrl: fields.fileUrl,
          previewUrl: fields.previewUrl,
          width: pixels?.width,
          height: pixels?.height,
          mimeType,
          extension,
        })
      );
    } catch (error) {
      console.error(error);
      uploadStatus.textContent = 'Upload failed. Please try again.';
    } finally {
      this.#setUploadBusy(false);
    }
  }

  /**
   * Shows the low-resolution warning, offering the upscale only when Cloudinary
   * can actually reach the target.
   *
   * @param {{ result: DpiResult, dimensions: { widthIn: number, heightIn: number }, requiredDpi: number }} check
   */
  #showWarning({ result, dimensions, requiredDpi }) {
    const { dpiWarning, dpiMessage, dpiUpscale, uploadStatus } = this.refs;

    uploadStatus.textContent = '';
    if (dpiMessage) dpiMessage.textContent = dpiWarningText(result, dimensions, requiredDpi);
    if (dpiUpscale) dpiUpscale.hidden = !result.upscale.possible;
    if (dpiWarning) dpiWarning.hidden = false;
    this.#syncUploadButton();
  }

  #clearWarning() {
    this.#pending = null;

    const { dpiWarning, dpiUpscale } = this.refs;
    if (dpiWarning) dpiWarning.hidden = true;
    if (dpiUpscale) dpiUpscale.hidden = false;
    this.#syncUploadButton();
  }

  /**
   * The panel's own upload button only earns its place when nothing else is
   * driving: the warning carries its own "Upload another file", and while a
   * transfer is in flight the status line owns the panel. Derived from state in
   * one spot so the two callers can't fight over `hidden`.
   */
  #syncUploadButton() {
    const { uploadButton, dpiWarning } = this.refs;
    uploadButton.hidden = this.#busy || dpiWarning?.hidden === false;
  }

  /** @param {boolean} busy */
  #setUploadBusy(busy) {
    this.#busy = busy;
    this.refs.uploadButton.disabled = busy;
    // Covers both warning buttons, so a second file can't be picked mid-upscale.
    if (this.refs.dpiWarning) this.refs.dpiWarning.inert = busy;
    this.#syncUploadButton();
  }

  #resetUpload() {
    this.refs.fileInput.value = '';
    this.refs.uploadStatus.textContent = '';
    this.#clearWarning();
  }
}

if (!customElements.get('artworks-modal')) customElements.define('artworks-modal', ArtworksModal);

/** Card class sets keyed by the gallery's `variant` attribute. */
const CARD_CLASSES = {
  profile: {
    figure: 'sb-profile__artwork-card',
    image: 'sb-profile__artwork-preview',
    caption: 'sb-profile__artwork-id',
    media: 'sb-profile__artwork-media',
    badge: 'sb-profile__artwork-badge',
  },
  modal: {
    figure: 'artworks-modal__card',
    image: 'artworks-modal__preview',
    caption: 'artworks-modal__id',
    media: 'artworks-modal__media',
    badge: 'artworks-modal__badge',
  },
};

const DEFAULT_PAGE_SIZE = 12;

/**
 * @typedef {object} ArtworksGalleryRefs
 * @property {HTMLElement} grid
 * @property {HTMLElement} status
 * @property {HTMLInputElement} [search]
 * @property {HTMLButtonElement} [prev]
 * @property {HTMLButtonElement} [next]
 */

/**
 * Loads a customer's artworks and renders them as <artwork-card> tiles, with
 * debounced search and cursor-based pagination. Used standalone on the profile
 * page and embedded inside <artworks-modal>.
 *
 * Attributes:
 *   - `selectable` — render clickable tiles that dispatch `artwork:selected`
 *   - `variant`    — card class set: 'profile' (default) or 'modal'
 *   - `manual`     — skip the auto-load on connect; load via `refresh()` instead
 *   - `page-size`  — page size (the `first` query param); defaults to 24
 *   - `empty-text` — message shown when the customer has no artworks
 *
 * Optional refs `search`, `prev`, `next` light up search / pagination when present.
 *
 * @extends Component<ArtworksGalleryRefs>
 */
export class ArtworksGallery extends Component {
  requiredRefs = ['grid', 'status'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** Forward-only API: track the `after` cursor of each visited page so we can step back. */
  #pageCursors = [/** @type {string | null} */ (null)];

  #searchTerm = '';

  /** @type {PageInfo | null} */
  #pageInfo = null;

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('manual')) this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  get customerId() {
    return this.getAttribute('customer-id');
  }

  get #pageSize() {
    return Number(this.getAttribute('page-size')) || DEFAULT_PAGE_SIZE;
  }

  /** Re-pulls from a clean state (first page, no search) — used on open / after upload. */
  refresh() {
    this.#searchTerm = '';
    if (this.refs.search) this.refs.search.value = '';
    this.#pageCursors = [null];
    this.#load();
  }

  /** Reloads the current page/search unchanged (error retry). */
  retry() {
    this.#load();
  }

  /** @param {Event} event */
  handleSearch(event) {
    this.#searchTerm = /** @type {HTMLInputElement} */ (event.target).value.trim();
    this.#debouncedSearch();
  }

  #debouncedSearch = debounce(() => {
    this.#pageCursors = [null];
    this.#load();
  }, 300);

  prev() {
    if (this.#pageCursors.length <= 1) return;
    this.#pageCursors.pop();
    this.#load();
  }

  next() {
    if (!this.#pageInfo?.hasNextPage) return;
    this.#pageCursors.push(this.#pageInfo.endCursor);
    this.#load();
  }

  async #load() {
    const { grid, status } = this.refs;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    status.textContent = 'Loading…';
    grid.replaceChildren();
    // Lock the arrows until the new page resolves so rapid clicks can't
    // advance the cursor stack past the data we've actually loaded.
    if (this.refs.prev) this.refs.prev.disabled = true;
    if (this.refs.next) this.refs.next.disabled = true;

    try {
      const api = new ArtworksApi({ customerId: this.customerId });
      const { artworks, pageInfo } = await api.list({
        search: this.#searchTerm,
        first: this.#pageSize,
        after: this.#pageCursors[this.#pageCursors.length - 1],
        signal: this.#abortController.signal,
      });

      this.#pageInfo = pageInfo;
      this.#render(artworks);
      this.#updatePagination();
    } catch (error) {
      if (error.name === 'AbortError') return;
      renderLoadError(status, 'artworks-gallery/retry');
    }
  }

  /** @param {Artwork[]} artworks */
  #render(artworks) {
    const { grid, status } = this.refs;

    if (!artworks.length) {
      if (this.#searchTerm) {
        status.textContent = `No artworks match “${this.#searchTerm}”.`;
      } else if (!this.customerId) {
        // Guest with nothing saved this session — nudge them to sign in.
        status.textContent =
          'Sign in to save your artwork for future use and use previous order artwork.';
      } else {
        status.textContent = this.getAttribute('empty-text') || 'No artworks yet.';
      }
      return;
    }

    status.textContent = '';

    const classes = CARD_CLASSES[this.getAttribute('variant') ?? ''] ?? CARD_CLASSES.profile;
    const selectable = this.hasAttribute('selectable');
    const editable = this.hasAttribute('editable');
    const customerId = this.customerId;
    const cards = artworks.map((artwork) =>
      createArtworkCard(artwork, { selectable, editable, customerId, classes })
    );
    grid.replaceChildren(...cards);
  }

  #updatePagination() {
    const { prev, next } = this.refs;
    if (prev) prev.disabled = this.#pageCursors.length <= 1;
    if (next) next.disabled = !this.#pageInfo?.hasNextPage;
  }
}

if (!customElements.get('artworks-gallery')) customElements.define('artworks-gallery', ArtworksGallery);

/**
 * The Upload / Saved-artworks button pair. Opens the shared, globally-rendered
 * <artworks-modal> (via {@link window.SBArtworks}) as its requester, then — when
 * an artwork is chosen — writes the preview + file URLs as line-item properties
 * onto a target product form and shows a preview.
 *
 * Attributes:
 *   - `product-form-id`       — id of the <form> to receive the properties; falls back to the nearest ancestor form
 *   - `property-name`         — property holding the preview URL (default "Uploaded file")
 *   - `property-name-hidden`  — property holding the file URL (default "_Uploaded file (direct link)")
 *   - `file-preview-selector` — element to render the preview into; defaults to an inline preview inside the component
 *   - `required-dpi`          — DPI an uploaded raster must reach at the chosen print size (default 300)
 *
 * @extends Component
 */
export class ArtworkActions extends Component {
  /**
   * Pixel dimensions of the current selection, when known. Print resolution
   * depends on these *and* the chosen size, so it has to be re-derived whenever
   * either moves — see #revalidate.
   *
   * @type {{ width: number, height: number } | null}
   */
  #pixels = null;

  /** Type of the current selection — decides upscale eligibility. @type {string} */
  #mimeType = '';

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ArtworkSelectedEvent.eventName, this.#handleSelected);
    // The product form recalculates on every size/quantity/material change and
    // announces it once its hidden inputs are current.
    this.#targetForm()?.addEventListener('product-form:recalculated', this.#revalidate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ArtworkSelectedEvent.eventName, this.#handleSelected);
    this.#targetForm()?.removeEventListener('product-form:recalculated', this.#revalidate);
  }

  get productFormId() {
    return this.getAttribute('product-form-id');
  }

  get propertyName() {
    return this.getAttribute('property-name') || 'Uploaded file';
  }

  get propertyNameHidden() {
    return this.getAttribute('property-name-hidden') || '_Uploaded file (direct link)';
  }

  get filePreviewSelector() {
    return this.getAttribute('file-preview-selector');
  }

  /**
   * Print size in inches for the artwork this component uploads, read live from
   * the target form's `properties[Size]` hidden input ("3x3", "5x2"). The
   * product form rewrites that input on every recalculation, so reading it at
   * the moment it's needed beats keeping a second copy of the state in sync.
   *
   * Null when the form tracks no size — which is what keeps the DPI check
   * scoped to the calculator PDP without any per-surface configuration.
   *
   * @returns {{ widthIn: number, heightIn: number } | null}
   */
  get printDimensions() {
    const input = this.#targetForm()?.querySelector('input[name="properties[Size]"]');
    if (!(input instanceof HTMLInputElement)) return null;

    const [width, height] = input.value.toLowerCase().split('x');
    const widthIn = parseFloat(width);
    const heightIn = parseFloat(height);
    if (!(widthIn > 0) || !(heightIn > 0)) return null;

    return { widthIn, heightIn };
  }

  /** @returns {number} DPI a raster upload must reach, from `required-dpi`. */
  get requiredDpi() {
    return Number(this.getAttribute('required-dpi')) || DEFAULT_REQUIRED_DPI;
  }

  /** Opens the shared modal on the Upload tab, with this element as requester. */
  openUpload() {
    window.SBArtworks?.open({ tab: 'upload', requester: this });
  }

  /** Opens the shared modal on the Gallery tab, with this element as requester. */
  openGallery() {
    window.SBArtworks?.open({ tab: 'gallery', requester: this });
  }

  /**
   * Programmatically selects an artwork — e.g. to restore a re-ordered line
   * item from the account page. Dispatches the same event a live selection
   * would, so the properties are written, the preview rendered, and the parent
   * product form unlocked. No-op when neither URL is provided.
   *
   * @param {{ previewUrl?: string, fileUrl?: string, width?: number, height?: number, mimeType?: string, extension?: string }} [artwork]
   */
  select({ previewUrl = '', fileUrl = '', width, height, mimeType, extension } = {}) {
    if (!previewUrl && !fileUrl) return;
    this.dispatchEvent(new ArtworkSelectedEvent({ previewUrl, fileUrl, width, height, mimeType, extension }));
  }

  /** @param {ArtworkSelectedEvent} event */
  #handleSelected = (event) => {
    const { fileUrl = '', previewUrl = '', width, height, mimeType } = event.detail ?? {};

    this.#pixels = width && height ? { width, height } : null;
    this.#mimeType = mimeType ?? '';

    const form = this.#targetForm();
    if (form) {
      this.#applyProperty(form, this.propertyName, previewUrl);
      this.#applyProperty(form, this.propertyNameHidden, fileUrl);
    }

    this.#renderPreview(previewUrl);
    this.#revalidate();
  };

  /**
   * Re-derives whether the current artwork still meets the DPI target and shows
   * or clears an inline notice. Runs on selection and on every form
   * recalculation, so shrinking the size back clears the notice too.
   *
   * Silent when we can't judge — unknown pixels, no print size, or a pass.
   */
  #revalidate = () => {
    const notice = this.querySelector('[data-artwork-notice]');
    if (!(notice instanceof HTMLElement)) return;

    const dimensions = this.printDimensions;
    if (!this.#pixels || !dimensions) {
      notice.hidden = true;
      return;
    }

    const requiredDpi = this.requiredDpi;
    // Passing the type matters: without it an animated GIF would be offered an
    // upscale that Cloudinary refuses.
    const result = evaluateDpi({ pixels: this.#pixels, dimensions, requiredDpi, type: this.#mimeType });

    notice.textContent = result.ok ? '' : dpiWarningText(result, dimensions, requiredDpi, { inline: true });
    notice.hidden = result.ok;
  };

  /** @returns {HTMLFormElement | null} */
  #targetForm() {
    const id = this.productFormId;
    if (id) {
      const el = document.getElementById(id);
      if (el instanceof HTMLFormElement) return el;
    }
    return this.closest('form');
  }

  /**
   * Writes a line-item property onto the form, reusing an existing hidden input
   * for that property when one is present.
   *
   * @param {HTMLFormElement} form
   * @param {string} name
   * @param {string} value
   */
  #applyProperty(form, name, value) {
    const inputName = `properties[${name}]`;
    let input = form.querySelector(`input[name="${inputName}"]`);
    if (!(input instanceof HTMLInputElement)) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = inputName;
      form.appendChild(input);
    }
    input.value = value;
  }

  /** @param {string} previewUrl */
  #renderPreview(previewUrl) {
    const target = this.filePreviewSelector
      ? document.querySelector(this.filePreviewSelector)
      : this.querySelector('[data-artwork-preview]');
    if (!(target instanceof HTMLElement)) return;

    const img = target instanceof HTMLImageElement ? target : target.querySelector('img');
    if (img instanceof HTMLImageElement && previewUrl) img.src = previewUrl;
    target.hidden = false;
  }
}

if (!customElements.get('artwork-actions')) customElements.define('artwork-actions', ArtworkActions);

/**
 * "Change Artwork" for a line item that is already in the cart.
 *
 * ArtworkActions above attaches artwork by writing hidden inputs into a product
 * form, which the add-to-cart FormData then carries. A cart line has no form, so
 * this writes straight to the Cart AJAX API instead and re-renders the cart the
 * same way a quantity change does. It is a sibling rather than a subclass because
 * everything that would need overriding there is `#`-private.
 *
 * Serves both carts from one write: Shopify is updated once, then the theme's own
 * section is re-rendered. Nothing here knows about Rebuy — its Smart Cart sees the
 * cart/change request itself and refreshes off the global listener in
 * snippets/custom-cart-extras, which needs the Cart provider setting on "Rebuy".
 *
 * Attributes:
 *   - `line-key`               — Shopify line item key, the change.js target
 *   - `line-properties`        — optional JSON of the line's current properties; with `line-quantity`, skips the cart read
 *   - `line-quantity`          — the line's current quantity; only read alongside `line-properties`
 *   - `width-in` / `height-in` — print size in inches; enables the DPI check
 *   - `property-name`          — property holding the preview URL (default "Uploaded file")
 *   - `property-name-hidden`   — property holding the file URL (default "_Uploaded file (direct link)")
 *   - `required-dpi`           — DPI an uploaded raster must reach (default 300)
 *
 * @extends Component
 */
export class ArtworkCartActions extends Component {
  /**
   * Puts the button back whenever a cart re-render strips it.
   *
   * `updatedCallback` is documented for this and cannot do it: morph hands
   * MORPH_OPTIONS.onAfterUpdate the node from the *incoming* HTML (morph.js), that HTML
   * is parsed with DOMParser (section-renderer.js), and a DOMParser document has no
   * custom element registry — so the node is never upgraded, `instanceof Component` is
   * false, and the callback never reaches the live element. The server renders this
   * element empty, so the button was being deleted on every unrelated cart update and
   * never rebuilt. A MutationObserver is told by the DOM rather than by the framework.
   *
   * Same mechanism as cart-attribute-checkbox and cart-rush-order.
   */
  #childListObserver = new MutationObserver(() => this.#render());

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    // Delegated from the host rather than bound to the button: morph preserves
    // this element in place but is free to destroy and rebuild its children, so a
    // listener on the button would be lost with it — and #render, seeing a button
    // already there, would never attach a new one. A dead button, silently.
    this.addEventListener('click', this.#handleClick);
    this.addEventListener(ArtworkSelectedEvent.eventName, this.#handleSelected);
    this.#childListObserver.observe(this, { childList: true });
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('button')) return;

    this.change();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#childListObserver.disconnect();
    this.removeEventListener(ArtworkSelectedEvent.eventName, this.#handleSelected);
  }

  /**
   * Builds the button, so a caller only has to place the element itself. No-op
   * once one is present — including a button a host rendered itself, which is why
   * the click is handled by delegation on the host rather than bound here.
   */
  #render() {
    if (this.querySelector('button')) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = `artwork-cart-actions__btn ${this.getAttribute('button-class') ?? ''}`.trim();
    button.textContent = this.getAttribute('label') || 'Change Artwork';
    button.setAttribute('aria-haspopup', 'dialog');

    // Distinguishes otherwise identical buttons for a screen reader moving
    // between lines. Left off when unknown so the visible text is used instead.
    const title = this.getAttribute('product-title');
    if (title) button.setAttribute('aria-label', `Change artwork for ${title}`);

    this.append(button);
  }

  /**
   * Read fresh on every use, never cached. Shopify rotates a line item's key
   * whenever the line's characteristics change — properties included — so our own
   * update gives this line a new key. The cart re-render copies the new value back
   * onto this element, which is what keeps a second change working.
   *
   * @returns {string}
   */
  get lineKey() {
    return this.getAttribute('line-key') || '';
  }

  get propertyName() {
    return this.getAttribute('property-name') || 'Uploaded file';
  }

  get propertyNameHidden() {
    return this.getAttribute('property-name-hidden') || '_Uploaded file (direct link)';
  }

  /** @returns {number} DPI a raster upload must reach, from `required-dpi`. */
  get requiredDpi() {
    return Number(this.getAttribute('required-dpi')) || DEFAULT_REQUIRED_DPI;
  }

  /**
   * Print size for this line, from the attributes the cart rendered out of the
   * line's own `Width`/`Height` properties. Read by ArtworksModal#checkResolution,
   * which is what gives the cart the same DPI warning and upscale offer as the PDP
   * without the modal needing to know where it was opened from.
   *
   * Null when the line tracks no size, which makes the modal skip the check.
   *
   * @returns {{ widthIn: number, heightIn: number } | null}
   */
  get printDimensions() {
    const widthIn = parseFloat(this.getAttribute('width-in') ?? '');
    const heightIn = parseFloat(this.getAttribute('height-in') ?? '');
    if (!(widthIn > 0) || !(heightIn > 0)) return null;

    return { widthIn, heightIn };
  }

  /** Opens the shared modal on the Gallery tab, with this element as requester. */
  change() {
    window.SBArtworks?.open({ tab: 'gallery', requester: this });
  }

  /** @param {ArtworkSelectedEvent} event */
  #handleSelected = (event) => {
    const { previewUrl = '', fileUrl = '' } = event.detail ?? {};
    if (!previewUrl && !fileUrl) return;

    this.#updateLine(previewUrl, fileUrl);
  };

  /**
   * Swaps the artwork properties on this line and gets the cart repainted.
   *
   * One write, wherever this is mounted. The only thing resolved first is where the
   * line's current properties come from: a `line-properties` attribute if a host
   * supplied one, else a cart read.
   *
   * @param {string} previewUrl
   * @param {string} fileUrl
   */
  async #updateLine(previewUrl, fileUrl) {
    this.#setBusy(true);

    try {
      const key = this.lineKey;

      const line = await this.#currentLine(key);
      if (!line) throw new Error(`Cart line ${key} not found`);

      const properties = this.#withArtwork(line.properties, previewUrl, fileUrl);

      const result = await this.#change(key, properties, line.quantity);
      this.#adoptKey(result, properties, previewUrl);

      // Tolerated rather than awaited-and-thrown: morphSection throws outright when
      // it can't find its section, and in a Rebuy cart there is no theme section to
      // find. The write has already landed, so a failed theme re-render must not be
      // reported as a failed artwork change.
      await this.#renderCart(result).catch((error) =>
        console.warn('[artwork-cart-actions] theme cart re-render failed:', error)
      );

      this.#restoreFocus();
    } catch (error) {
      // Nothing customer-facing: the button simply becomes clickable again.
      console.error('[artwork-cart-actions] failed to change artwork:', error);
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * The line's properties with the two artwork keys swapped in.
   *
   * change.js overwrites the whole properties object — any key we fail to resend
   * is erased — so this always builds on the full existing set.
   *
   * @param {Record<string, string>} current
   * @param {string} previewUrl
   * @param {string} fileUrl
   * @returns {Record<string, string>}
   */
  #withArtwork(current, previewUrl, fileUrl) {
    const properties = {
      ...current,
      [this.propertyName]: previewUrl,
      [this.propertyNameHidden]: fileUrl,
    };

    // sb-pdp.js mirrors every property into a `_properties` JSON blob, so the
    // artwork URL lives in two places on the same line. Keep them agreeing.
    if (typeof current._properties === 'string') {
      try {
        const aggregate = JSON.parse(current._properties);
        aggregate[this.propertyName] = previewUrl;
        aggregate[this.propertyNameHidden] = fileUrl;
        properties._properties = JSON.stringify(aggregate);
      } catch {
        // leave the aggregate alone if it isn't valid JSON
      }
    }

    return properties;
  }

  /**
   * The single write. Returns the updated cart JSON, which both UIs are then
   * refreshed from.
   *
   * Deliberately the only mutation, and worth keeping that way: a second write to
   * the same line — through a cart app's own API, say — would be aiming at a key
   * that no longer exists, since Shopify rotates a line's key whenever its
   * properties change.
   *
   * @param {string} key
   * @param {Record<string, string>} properties
   * @param {number} quantity - The line's current quantity, resent unchanged.
   * @returns {Promise<{ item_count: number, items?: { key: string, properties?: Record<string, string> }[], sections?: Record<string, string> }>}
   */
  async #change(key, properties, quantity) {
    // Ask for every mounted cart surface's section, as a quantity change does:
    // the others re-render themselves off the update event rather than each
    // having to fetch its own copy.
    const sections = new Set(
      [...document.querySelectorAll('cart-items-component')]
        .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
        .filter((id) => Boolean(id))
    );

    // `quantity` is required, not optional. Omitting it does NOT leave the line
    // alone — change.js resets the line to 1, which on a custom sticker changes
    // the price, since _customPrice is per-piece and the price table is
    // non-linear in quantity.
    //
    // Sending it means calculator-cart-validator.js now processes this request.
    // That is fine, and slightly desirable: it recomputes _customPrice for the
    // same quantity, finds it unchanged and returns early. It relies on that
    // validator merging onto `payload.properties` rather than its own /cart.js
    // read, or it would overwrite the artwork we're setting here.
    const body = JSON.stringify({
      id: key,
      quantity,
      properties,
      ...(sections.size ? { sections: [...sections].join(','), sections_url: window.location.pathname } : {}),
    });

    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    const result = await response.json();
    if (result.errors) {
      throw new Error(typeof result.errors === 'string' ? result.errors : 'Could not update the artwork');
    }

    return result;
  }

  /**
   * Re-points this element at the line's new key, and refreshes a supplied
   * property snapshot alongside it.
   *
   * Shopify rotates a line item's key whenever the line's characteristics change —
   * properties included — so the key we just wrote with is already stale, and a
   * second change would address a line that no longer exists. This is why changing
   * artwork twice in a row failed.
   *
   * The response already carries the updated cart, so no request is needed. Nor is
   * this left to the cart re-render: the morph does copy the fresh attribute over,
   * but there is no theme section to morph in the Rebuy cart, so the key would
   * never refresh there.
   *
   * Matching is on the exact property set first. Artwork URLs alone don't identify
   * the line — a custom sticker's size lives in its properties rather than its
   * variant, so two lines can legitimately share both artwork and variant — and an
   * exact match is unambiguous because Shopify merges lines agreeing on variant and
   * every property. The fallback covers Shopify normalising a value (it drops
   * empty-string properties, for one) and only accepts a single candidate, so an
   * ambiguous cart leaves the key alone rather than adopting the wrong line's.
   *
   * @param {{ items?: { key: string, properties?: Record<string, string> }[] }} result
   * @param {Record<string, string>} properties
   * @param {string} previewUrl
   */
  #adoptKey(result, properties, previewUrl) {
    const items = result.items ?? [];
    /** @param {Record<string, string>} [set] */
    const fingerprint = (set) => JSON.stringify(Object.entries(set ?? {}).sort());

    const wanted = fingerprint(properties);
    const exact = items.find((item) => fingerprint(item.properties) === wanted);

    const candidates = exact
      ? [exact]
      : items.filter((item) => item.properties?.[this.propertyName] === previewUrl);

    if (candidates.length === 1) {
      this.setAttribute('line-key', candidates[0].key);

      // A supplied snapshot is stale the moment we change the line, so a second
      // change would resend the artwork we just replaced. Refresh it rather than
      // dropping it, which would cost a cart read the host was avoiding.
      if (this.hasAttribute('line-properties')) {
        this.setAttribute('line-properties', JSON.stringify(candidates[0].properties ?? properties));
      }
      return;
    }

    console.warn(
      `[artwork-cart-actions] could not identify the updated line (${candidates.length} candidates); its key may be stale`
    );
  }

  /**
   * The properties a host handed us via `line-properties`, saving a cart read.
   * Suits a template rendering from its own live cart state.
   *
   * A malformed attribute returns null so the caller falls back to a real read:
   * sending a half-parsed set would silently drop properties off the line.
   *
   * @returns {Record<string, string> | null}
   */
  #suppliedProperties() {
    const supplied = this.getAttribute('line-properties');
    if (!supplied) return null;

    try {
      const parsed = JSON.parse(supplied);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    } catch {
      console.warn('[artwork-cart-actions] unparseable line-properties, reading the cart instead');
    }

    return null;
  }

  /**
   * The line's properties and quantity, from the cheapest source that has both.
   *
   * Quantity matters as much as the properties: change.js resets a line to 1 when
   * the request omits it, which on a custom sticker silently changes the price.
   *
   * `line-properties` and `line-quantity` are treated as a pair — supplying only
   * one still costs the cart read, so there's nothing to gain from honouring it
   * alone. A host rendering from live cart state should pass both; without them this
   * costs one read per change, which is the price of knowing nothing about the host.
   *
   * @param {string} key
   * @returns {Promise<{ properties: Record<string, string>, quantity: number } | null>}
   */
  async #currentLine(key) {
    const supplied = this.#suppliedProperties();
    const suppliedQuantity = Number(this.getAttribute('line-quantity'));
    if (supplied && suppliedQuantity > 0) return { properties: supplied, quantity: suppliedQuantity };

    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
    const item = cart.items?.find((/** @type {{ key: string }} */ line) => line.key === key);
    if (!item) return null;

    return { properties: item.properties ?? {}, quantity: item.quantity };
  }

  /**
   * The modal closes before it hands us the selection, so the native <dialog>
   * restores focus to our button — and then the morph throws that button away.
   * Put focus back on the rebuilt one, but only if nothing else has claimed it.
   */
  #restoreFocus() {
    if (document.activeElement && document.activeElement !== document.body) return;

    // The rebuild normally lands before this, but it arrives on a microtask from the
    // observer — so make sure there is a button rather than depending on that ordering.
    this.#render();
    this.querySelector('button')?.focus();
  }

  /**
   * Re-renders the cart through the same channel a quantity change uses: the
   * update event on the cart component itself, then an explicit morph.
   *
   * Both halves matter. Dispatching from this element instead would leave
   * CartItemsComponent's own handler unable to self-skip, falling through to a
   * default 'full' morph — and in drawer mode that section is the *header*, so
   * every artwork change would re-morph the header and its popover state. And
   * `itemCount` has to be passed or cart-icon.js reads it as 0 and hides the
   * cart bubble.
   *
   * @param {{ item_count: number, sections?: Record<string, string> }} result
   */
  async #renderCart(result) {
    const cartItems = this.#cartItems();
    const sectionId = cartItems?.sectionId;
    if (!cartItems || !sectionId) return;

    cartItems.dispatchEvent(
      new CartUpdateEvent(result, sectionId, {
        itemCount: result.item_count,
        source: 'artwork-cart-actions',
        sections: result.sections,
      })
    );

    const html = result.sections?.[sectionId];
    if (html) await morphSection(sectionId, html, cartItems.isDrawer ? 'hydration' : 'full');
  }

  /**
   * @returns {(HTMLElement & { sectionId: string, isDrawer: boolean }) | null}
   */
  #cartItems() {
    const element = this.closest('cart-items-component');
    if (!(element instanceof HTMLElement) || !('sectionId' in element)) return null;

    return /** @type {HTMLElement & { sectionId: string, isDrawer: boolean }} */ (element);
  }

  /**
   * Nodes are queried fresh rather than held: a cart re-render can replace them
   * between the start and end of an update.
   *
   * @param {boolean} busy
   */
  #setBusy(busy) {
    const button = this.querySelector('button');
    if (button instanceof HTMLButtonElement) {
      button.disabled = busy;
      button.setAttribute('aria-busy', String(busy));
    }

    // Same class the cart uses to freeze its own controls mid-update.
    this.#cartItems()?.classList.toggle('cart-items-disabled', busy);
  }
}

if (!customElements.get('artwork-cart-actions')) {
  customElements.define('artwork-cart-actions', ArtworkCartActions);
}

/**
 * Global entry point for opening the shared artwork library modal. The modal is
 * rendered once (see snippets/customer-artworks.liquid in theme.liquid); this
 * lets any button or component open it without a direct reference.
 */
window.SBArtworks = window.SBArtworks ?? {
  /** @param {{ tab?: 'gallery' | 'upload', requester?: HTMLElement | null }} [options] */
  open(options = {}) {
    const modal = document.querySelector('artworks-modal');
    if (modal instanceof ArtworksModal) modal.open(options.tab ?? 'gallery', options.requester ?? null);
  },

  /** @returns {ArtworksModal | null} */
  get modal() {
    const modal = document.querySelector('artworks-modal');
    return modal instanceof ArtworksModal ? modal : null;
  },
};
